import { registerPlugin } from '@capacitor/core'
import { getNativePlatform, isNativeApp } from '../../utils/platform'
import type { ChimeAttendeeInfo, ChimeMeetingInfo, ChimeJoinTokenResponse } from '../chime-service'

/**
 * NATIVE iOS call AUDIO via the Amazon Chime SDK for iOS (Swift).
 *
 * Root cause this fixes: WebKit deliberately mutes microphone capture in a
 * backgrounded WKWebView for non-Safari apps (WebKit changeset 268791 /
 * bug 233419), and on a locked-screen answer the whole WebContent process is
 * suspended — so the JS Chime/WebRTC media engine can't start mic capture. The
 * native Chime SDK's `audioVideo.start(callKitEnabled: true)` is the
 * Apple-supported way to start a recording session from the background inside
 * CallKit's `provider(_:didActivate:)`. The native attendee joins the SAME
 * Chime meeting as the JS/Android peers (same JoinInfo the `chime-meeting-token`
 * endpoint mints), so recording/transcription/signaling and the 20-participant
 * cap are all preserved.
 *
 * SCOPE: iOS VOICE calls (incoming AND outgoing). VIDEO stays on the JS
 * ChimeCallAdapter (an iOS video call keeps today's behavior), and WEB +
 * ANDROID are entirely unchanged. The Swift side (ChimeMediaPlugin /
 * ChimeMediaController) is audio-only for now but is structured to extend
 * to video later.
 */
type ChimeMediaPluginApi = {
  /** True when the native plugin is compiled into this build (iOS ≥ build 15). */
  isAvailable(): Promise<{ available: boolean }>
  /**
   * Join the Chime meeting NATIVELY and start audio. `callKitAnswer` tells the
   * native side this join came from a CallKit answer, so it starts audio from
   * `provider(_:didActivate:)` (`audioVideo.start(callKitEnabled: true)`) rather
   * than immediately — the whole point of the fix. For foreground/outgoing joins
   * pass `false` and the SDK owns its own session and starts immediately.
   */
  joinNative(options: {
    callId: string
    meeting: ChimeMeetingInfo
    attendee: ChimeAttendeeInfo
    callKitAnswer: boolean
    /** Group/room call: native never applies the 1:1 remote-left backstop. */
    group?: boolean
  }): Promise<{ joined: boolean }>
  /** Leave the native meeting + stop the audio session for this call. */
  leaveNative(options: { callId?: string }): Promise<void>
  /** Mute/unmute the local mic on the native audio unit. */
  setMuted(options: { muted: boolean }): Promise<{ muted: boolean }>
  /** Route audio to the loud speaker (true) or earpiece/headset (false). */
  setSpeaker(options: { speaker: boolean }): Promise<{ speaker: boolean }>
  /**
   * Mirror the JS kill switch into native UserDefaults so the LOCKED-screen
   * native mint path (which runs with the WebView suspended) honors it.
   */
  syncEnabled(options: { enabled: boolean }): Promise<{ enabled: boolean }>
  /**
   * The call (if any) whose audio the native Chime session currently owns:
   *  - `connected`: local native audio has started (audioSessionDidStart).
   *  - `remotePresent`: a remote human attendee is present in the native Chime
   *    roster (two-way media) — the canonical connect moment, used on
   *    foreground-adopt to flip an already-talking locked-answer call to
   *    'active' immediately.
   * So JS doesn't double-join on foreground a call the native side already
   * started while the phone was locked.
   */
  activeCall(): Promise<{ callId?: string; connected?: boolean; remotePresent?: boolean }>
  addListener(
    eventName: 'nativeMediaConnected',
    listener: (data: { callId: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'nativeMediaFailed',
    listener: (data: { callId: string; code: number; message: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'nativeRosterChanged',
    listener: (data: { callId: string; remoteCount: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>
  addListener(
    eventName: 'nativeRemoteLeft',
    listener: (data: { callId: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

const ChimeMedia = registerPlugin<ChimeMediaPluginApi>('ChimeMedia')

/** localStorage kill-switch key. Set to '0'/'false'/'off' to force JS media. */
const KILL_SWITCH_KEY = 'ascend-native-ios-media'

/**
 * Compile-time default: native iOS audio is ON for iOS voice calls. Flip this
 * constant (or ship the localStorage override) to roll back without a native
 * rebuild. The localStorage override wins over the default in BOTH directions
 * so the path can be forced on for testing or off in an incident.
 */
const NATIVE_IOS_MEDIA_DEFAULT_ON = true

function readOverride(): boolean | null {
  try {
    const raw = window.localStorage.getItem(KILL_SWITCH_KEY)
    if (raw == null) return null
    const v = raw.trim().toLowerCase()
    if (v === '1' || v === 'true' || v === 'on' || v === 'yes') return true
    if (v === '0' || v === 'false' || v === 'off' || v === 'no') return false
    return null
  } catch {
    return null
  }
}

/**
 * Feature gate + kill switch: is the NATIVE iOS audio path enabled right now?
 * Only ever true on the iOS native shell. `localStorage['ascend-native-ios-media']`
 * overrides the compile-time default in either direction.
 */
export function isNativeIosAudioEnabled(): boolean {
  if (!isNativeApp() || getNativePlatform() !== 'ios') return false
  const override = readOverride()
  if (override !== null) return override
  return NATIVE_IOS_MEDIA_DEFAULT_ON
}

let availabilityCache: boolean | null = null

/** Whether the ChimeMedia native plugin is present on the bridge (iOS build 15+). */
export async function isNativeIosMediaAvailable(): Promise<boolean> {
  if (!isNativeIosAudioEnabled()) return false
  if (availabilityCache !== null) return availabilityCache
  try {
    const { available } = await ChimeMedia.isAvailable()
    availabilityCache = available
    return available
  } catch {
    availabilityCache = false
    return false
  }
}

/**
 * Join the Chime meeting through the native SDK. Feeds the SAME JoinInfo the
 * JS path uses (`credentials.meeting.Meeting` / `credentials.attendee.Attendee`).
 * Throws if the bridge rejects so the caller can fall back to the JS adapter.
 */
export async function joinNativeIosMedia(input: {
  callId: string
  credentials: ChimeJoinTokenResponse
  callKitAnswer: boolean
  group?: boolean
}): Promise<void> {
  const meeting = input.credentials.meeting?.Meeting
  const attendee = input.credentials.attendee?.Attendee
  if (!meeting || !attendee) {
    throw new Error('Incomplete meeting credentials for native iOS media')
  }
  await ChimeMedia.joinNative({
    callId: input.callId,
    meeting,
    attendee,
    callKitAnswer: input.callKitAnswer,
    group: Boolean(input.group),
  })
}

/**
 * Mirror the JS kill switch into native UserDefaults so the native locked-answer
 * mint path (WebView suspended) honors an explicit override. Best-effort.
 */
export async function syncNativeIosMediaEnabled(): Promise<void> {
  if (!isNativeApp() || getNativePlatform() !== 'ios') return
  try {
    await ChimeMedia.syncEnabled({ enabled: isNativeIosAudioEnabled() })
  } catch {
    /* plugin missing (older build) — native default applies */
  }
}

/**
 * The call the native Chime session currently owns (and whether it connected),
 * or null. Used on foreground to avoid double-joining a call the native side
 * already started while locked. Never throws.
 */
export async function getNativeIosMediaActiveCall(): Promise<{
  callId: string
  connected: boolean
  remotePresent: boolean
} | null> {
  if (!isNativeApp() || getNativePlatform() !== 'ios') return null
  try {
    const res = await ChimeMedia.activeCall()
    if (res && typeof res.callId === 'string' && res.callId) {
      return {
        callId: res.callId,
        connected: Boolean(res.connected),
        remotePresent: Boolean(res.remotePresent),
      }
    }
    return null
  } catch {
    return null
  }
}

/** Leave + stop the native audio session (idempotent; safe to over-call). */
export async function leaveNativeIosMedia(callId?: string): Promise<void> {
  if (!isNativeApp() || getNativePlatform() !== 'ios') return
  try {
    await ChimeMedia.leaveNative({ callId })
  } catch {
    /* nothing to leave */
  }
}

/** Mute/unmute the native mic. Best-effort. */
export async function setNativeIosMediaMuted(muted: boolean): Promise<void> {
  try {
    await ChimeMedia.setMuted({ muted })
  } catch {
    /* plugin missing / no active native session */
  }
}

/** Route native audio to speaker (true) or earpiece (false). Returns success. */
export async function setNativeIosMediaSpeaker(speaker: boolean): Promise<boolean> {
  try {
    await ChimeMedia.setSpeaker({ speaker })
    return true
  } catch {
    return false
  }
}

/**
 * Subscribe to native media lifecycle events. Returns an unsubscribe function
 * that removes every listener. No-op (empty unsubscribe) off iOS.
 */
export async function addNativeIosMediaListeners(handlers: {
  onConnected: (callId: string) => void
  onFailed: (callId: string, code: number, message: string) => void
  onRosterChanged: (callId: string, remoteCount: number) => void
  onRemoteLeft: (callId: string) => void
}): Promise<() => void> {
  if (!isNativeApp() || getNativePlatform() !== 'ios') return () => {}
  try {
    const handles = await Promise.all([
      ChimeMedia.addListener('nativeMediaConnected', ({ callId }) => {
        if (callId) handlers.onConnected(callId)
      }),
      ChimeMedia.addListener('nativeMediaFailed', ({ callId, code, message }) => {
        if (callId) handlers.onFailed(callId, code ?? -1, message ?? '')
      }),
      ChimeMedia.addListener('nativeRosterChanged', ({ callId, remoteCount }) => {
        if (callId) handlers.onRosterChanged(callId, remoteCount ?? 0)
      }),
      ChimeMedia.addListener('nativeRemoteLeft', ({ callId }) => {
        if (callId) handlers.onRemoteLeft(callId)
      }),
    ])
    return () => {
      for (const h of handles) void h.remove()
    }
  } catch {
    return () => {}
  }
}
